/**
 * Project session lifecycle: opening/creating projects through the project store, the editing
 * lock, autosave, attaching media and imports. Contains no storage details itself.
 */
import {
  addMedia,
  ensureMediaFolder,
  moveMediaToFolder,
  replaceMedia,
  type Id,
  type MediaAsset,
  type MediaOrigin,
  type Project,
} from '@ve/core';
import { create } from 'zustand';
import { mediaService } from '../media/setup.ts';
import { useMediaRuntime } from '../media/service.ts';
import { decideDuplicates } from '../media/duplicates.ts';
import type { ImportSource } from '../media/types.ts';
import { askDuplicate } from './duplicate-prompt.ts';
import { projectStore } from '../projects/setup.ts';
import { ProjectStoreError, type NewProject, type PickedLocation } from '../projects/types.ts';
import { edit, useEditor } from './editor-store.ts';
import { errorMessage, toast } from './toast-store.ts';
import { useUi } from './ui-store.ts';

export type SaveStatus = 'saved' | 'dirty' | 'saving' | 'error';

export const useSaveStatus = create<{ status: SaveStatus; lastSavedAt: string | null }>()(() => ({
  status: 'saved',
  lastSavedAt: null,
}));

const AUTOSAVE_DELAY_MS = 800;
let saveTimer: ReturnType<typeof setTimeout> | null = null;
let saving: Promise<void> | null = null;
let releaseLock: (() => void) | null = null;
let unsubscribeAutosave: (() => void) | null = null;
let unsubscribeMediaSync: (() => void) | null = null;

/** Starts editing a loaded project. Fails if it is already open in another tab. */
async function startSession(project: Project): Promise<void> {
  await saveNow();
  closeProject();
  const release = await projectStore.lock(project.id);
  if (!release) {
    throw new ProjectStoreError(`“${project.name}” is already open in another tab or window`, 'locked');
  }
  releaseLock = release;
  useEditor.getState().load(project);
  await mediaService.attach(project);
  unsubscribeMediaSync = useEditor.getState().doc!.subscribe((p) => mediaService.sync(p));
  startAutosave();
  // Ask the browser not to evict caches and the recent-projects list under storage pressure.
  void navigator.storage?.persist?.().catch(() => undefined);
  window.location.hash = `#/project/${project.id}`;
  document.title = `${project.name} — Cutroom`;
}

/** Opens a recently used project (may prompt for folder access: call from a user gesture). */
export async function openRecentProject(projectId: string): Promise<void> {
  await startSession(await projectStore.openRecent(projectId));
}

/** Lets the user pick a project folder. Resolves to null if cancelled. */
export function pickProjectLocation(purpose: 'open' | 'create'): Promise<PickedLocation | null> {
  return projectStore.pickLocation(purpose);
}

export async function openProjectAt(location: PickedLocation): Promise<void> {
  await startSession(await projectStore.openAt(location));
}

export async function createProjectAt(
  location: PickedLocation,
  init: NewProject,
  options?: { inPlace?: boolean },
): Promise<void> {
  await startSession(await projectStore.createAt(location, init, options));
}

/**
 * Opens a project file the operating system launched the app with. Resolves to false if the user
 * cancelled locating its folder.
 */
export async function openLaunchedProject(file: FileSystemFileHandle): Promise<boolean> {
  if (!projectStore.openLaunched) throw new Error('This project store cannot open project files');
  const project = await projectStore.openLaunched(file);
  if (!project) return false;
  await startSession(project);
  return true;
}

export function closeProject(): void {
  if (saveTimer) clearTimeout(saveTimer);
  saveTimer = null;
  unsubscribeAutosave?.();
  unsubscribeAutosave = null;
  unsubscribeMediaSync?.();
  unsubscribeMediaSync = null;
  mediaService.detach();
  useUi.getState().setDialog(null); // dialogs refer to the project being closed
  useUi.getState().setLibraryFolder(undefined);
  useEditor.getState().close();
  releaseLock?.();
  releaseLock = null;
  useSaveStatus.setState({ status: 'saved', lastSavedAt: null });
  document.title = 'Cutroom';
}

function startAutosave(): void {
  const doc = useEditor.getState().doc;
  if (!doc) return;
  unsubscribeAutosave = doc.subscribe((_p, reason) => {
    if (reason === 'load') return;
    useSaveStatus.setState({ status: 'dirty' });
    if (saveTimer) clearTimeout(saveTimer);
    saveTimer = setTimeout(() => void saveNow(), AUTOSAVE_DELAY_MS);
  });
}

/** Saves immediately (also used on Cmd+S, before closing and before unload). */
export async function saveNow(): Promise<void> {
  if (saveTimer) clearTimeout(saveTimer);
  saveTimer = null;
  const doc = useEditor.getState().doc;
  if (!doc || !doc.isDirty) return;
  if (saving) await saving;
  const revision = doc.version;
  const project = { ...doc.project, updatedAt: new Date().toISOString() };
  useSaveStatus.setState({ status: 'saving' });
  saving = projectStore
    .save(project)
    .then(() => {
      doc.markSaved(revision);
      useSaveStatus.setState({ status: doc.isDirty ? 'dirty' : 'saved', lastSavedAt: project.updatedAt });
    })
    .catch((err: unknown) => {
      useSaveStatus.setState({ status: 'error' });
      toast.error(`Saving failed: ${errorMessage(err)}`);
    })
    .finally(() => {
      saving = null;
    });
  await saving;
}

// ---------------------------------------------------------------------------------------------
// Imports
// ---------------------------------------------------------------------------------------------

/** File name and size of an import source, for duplicate detection (size if cheaply known). */
async function describeSource(source: ImportSource): Promise<{ fileName: string; size: number | undefined }> {
  if (source.kind === 'blob') return { fileName: source.name, size: source.blob.size };
  try {
    return { fileName: source.handle.name, size: (await source.handle.getFile()).size };
  } catch {
    return { fileName: source.handle.name, size: undefined };
  }
}

/**
 * Imports media through the active storage provider (copying into or referencing from the
 * project folder), analyses it and adds it to the library in one undoable edit.
 * Media lands in `folderId` (default: the library folder being browsed); dropped directories are
 * mirrored as sub-folders there. Files already in the library are asked about (replace, keep the
 * existing item, or add anyway). Failed items are reported as toasts and skipped.
 *
 * Returns the media for each item, in order: newly imported, or the existing library item the
 * user chose to keep (so a timeline drop can still place it).
 */
export async function importMedia(
  items: Array<{ source: ImportSource; origin?: MediaOrigin }>,
  onItemProgress?: (index: number, fraction: number) => void,
  folderId: Id | undefined = useUi.getState().libraryFolderId,
): Promise<MediaAsset[]> {
  const project = useEditor.getState().project;
  if (!project) return [];
  const described = await Promise.all(
    items.map(async (item, index) => ({ item: index, ...(await describeSource(item.source)) })),
  );
  const decisions = await decideDuplicates(project, described, askDuplicate);
  const toImport = decisions.filter((d) => d.action !== 'keep');
  for (const d of decisions) if (d.action === 'keep') onItemProgress?.(d.item, 1);

  const results = await mediaService.importSources(
    toImport.map((d) => items[d.item]!),
    (name, err) => toast.error(`Import of ${name} failed: ${errorMessage(err)}`),
    onItemProgress && ((i, fraction) => onItemProgress(toImport[i]!.item, fraction)),
    (name, warning) => toast.warn(`${name}: ${warning}`),
  );
  const imported = new Map<number, MediaAsset>();
  results.forEach((asset, i) => asset && imported.set(toImport[i]!.item, asset));

  if (imported.size > 0) {
    const first = [...imported.values()][0]!;
    edit(imported.size === 1 ? `Import ${first.name}` : `Import ${imported.size} files`, (p) => {
      // The folder may have been deleted while the files were being analysed.
      const base = folderId !== undefined && p.mediaFolders[folderId] ? folderId : undefined;
      for (const decision of toImport) {
        const asset = imported.get(decision.item);
        if (!asset) continue;
        const old = decision.action === 'replace' ? p.media[decision.existing.id] : undefined;
        if (old && old.kind === asset.kind) {
          replaceMedia(p, old.id, asset);
          continue;
        }
        if (old) toast.warn(`“${old.name}” is a different kind of media, so ${asset.name} was added instead`);
        addMedia(p, asset);
        const source = items[decision.item]!.source;
        let target = base;
        if (source.kind === 'handle')
          for (const name of source.libraryPath ?? []) target = ensureMediaFolder(p, name, target);
        if (target !== undefined) moveMediaToFolder(p, [asset.id], target);
      }
    });
  }

  const current = useEditor.getState().project;
  return decisions
    .map((d) => (d.action === 'keep' ? current?.media[d.existing.id] : imported.get(d.item)))
    .filter((a): a is MediaAsset => !!a);
}

/** Opens the import provider's file picker and imports the chosen files. */
export async function pickAndImport(): Promise<MediaAsset[]> {
  const project = useEditor.getState().project;
  const provider = mediaService.registry.importProvider;
  if (!project || !provider.pickFiles || !importReady()) return [];
  try {
    const sources = await provider.pickFiles(project.id);
    return importMedia(sources.map((source) => ({ source })));
  } catch (err) {
    toast.error(`Could not open the file picker: ${errorMessage(err)}`);
    return [];
  }
}

/**
 * Reads importable sources from a drop event. Call synchronously inside the drop handler; returns
 * null when nothing importable was dropped or the project folder is not accessible.
 */
export function sourcesFromDrop(dataTransfer: DataTransfer): Promise<ImportSource[]> | null {
  const provider = mediaService.registry.importProvider;
  if (!provider.sourcesFromDrop || !dataTransfer.types.includes('Files')) return null;
  if (!importReady()) return null;
  return provider.sourcesFromDrop(dataTransfer);
}

/** True when media can be imported; otherwise explains why not. */
export function importReady(): boolean {
  const access = useMediaRuntime.getState().access[mediaService.registry.importProvider.id];
  if (access?.state === 'ready') return true;
  toast.info('The project folder is not accessible — reconnect it at the top of the library');
  return false;
}

window.addEventListener('beforeunload', (e) => {
  if (useEditor.getState().doc?.isDirty) {
    void saveNow();
    e.preventDefault();
  }
});
